
import React, { useEffect, useState } from 'react'

import { trigger } from '../events/customEvents'

const SessionContext = React.createContext()
const { Provider } = SessionContext

const SESSION_STORAGE_KEY = 'garagesale_session'

const defaultSession =
{
	actualUserId: 0,
	actualUserName: null,
	actualUserRoleTypeId: 0,
	apiServerName: '',
	authenticationResult:
	{
		authenticationResultMessage:    '',
		authenticationResultMessageKey: '',
		authenticationResultType:       0
	},
	defaultRoute: '',
	isAuthenticated: false,
	isImpersonating: false,
	user:
	{
		allowEmailForwarding: false,
		cellValidated: false,
		dealerId: 0,
		emailAddress: '',
		emailValidated: false,
		emailValidationExpiry: null,
		fullName: null,
		hasAcceptedEula: false,
		hasCustomerDataVisibility: false,
		isLockedOut: false,
		lastLogin: null,
		mustChangePassword: false,
		oemId: 0,
		preferredLanguage: 'en',
		sessionExpiry: null,
		sessionKey: '',
		twoFactorAuthExpiry: null,
		userDevices: null,
		userId: 0,
		userImpersonations: [],
		userName: '',
		userState: 0,
		usersRoleMemberships: [{
			userId: 0,
			userRoleId: 0,
			usersRoleMembershipId: 0,
			userRole:
			{
				hidden: false,
				roleName: null,
				userGroupId: 0,
				userRoleId: 0,
				userRoleTypeId: 0,
				userRoleType:
					{
						description: '',
						userRoleTypeId: 0
					}
			}
		}],
		userSession:
		{
			userSessionId: '',
			applicationId: '',
			userId: 0,
			sessionKey: '',
			sessionExpiry: new Date(),
			timeCreated: new Date(),
			timeUpdated: new Date()
		}
	}
}

let sessionTimer = null
let sessionTimeoutSeconds = 0
let secondsBeforePrompt = 0
let sessionTimeoutDisplay = { minutes: 0, seconds: 0 }

/**
 * Load session from localStorage if it exists and hasn't expired
 */
const loadPersistedSession = () =>
{
	try
	{
		const storedSession = localStorage.getItem(SESSION_STORAGE_KEY)
		if (storedSession)
		{
			const session = JSON.parse(storedSession)
			// Check if session has expired
			if (session.user?.userSession?.sessionExpiry)
			{
				const expiryTime = new Date(session.user.userSession.sessionExpiry)
				const now = new Date()
				if (expiryTime > now && session.isAuthenticated)
				{
					return session
				}
				else
				{
					// Session expired, clear it
					localStorage.removeItem(SESSION_STORAGE_KEY)
				}
			}
		}
	}
	catch (error)
	{
		console.error('Error loading persisted session:', error)
		localStorage.removeItem(SESSION_STORAGE_KEY)
	}
	return null
}

/**
 * Save session to localStorage
 */
const persistSession = (session) =>
{
	try
	{
		if (session.isAuthenticated)
		{
			localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
		}
		else
		{
			localStorage.removeItem(SESSION_STORAGE_KEY)
		}
	}
	catch (error)
	{
		console.error('Error persisting session:', error)
	}
}

const SessionProvider = (props) =>
{
	// Initialize state from persisted session or default
	const [loginSession, setLoginSession] = useState(() => {
		const persistedSession = loadPersistedSession()
		return persistedSession || defaultSession
	})
	const [showAdminTestingControls, setShowAdminTestingControls] = useState(false)

	// On mount, if we loaded a persisted session, start the timer
	useEffect(() =>
	{
		if (loginSession.isAuthenticated)
		{
			resetSessionTimer(loginSession)
		}
		return () => clearInterval(sessionTimer)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const sessionDurationInSeconds = (expiryTime) =>
	{
		let t1 = new Date(expiryTime)
		let t2 = new Date()

		let timeDiff = t1.getTime() - t2.getTime()
		let durationSeconds = timeDiff / 1000

		return Math.abs(durationSeconds)
	}

	const countdownSession = () =>
	{
		sessionTimeoutSeconds = sessionTimeoutSeconds - 1
		secondsBeforePrompt = secondsBeforePrompt - 1

		let sessionTimeout = new Date(sessionTimeoutSeconds * 1000)
		let minutes = sessionTimeout.getUTCMinutes()
		let seconds = sessionTimeout.getUTCSeconds()

		sessionTimeoutDisplay = {
			minutes: (minutes.toString().length === 1) ? '0' + minutes : minutes,
			seconds: (seconds.toString().length === 1) ? '0' + seconds : seconds
		}

		if (secondsBeforePrompt <= 0)
		{
			document.title = 'Session ' + sessionTimeoutDisplay.minutes + ':' + sessionTimeoutDisplay.seconds
		}

		if (sessionTimeoutSeconds <= 0)
		{
			clearInterval(sessionTimer)

			document.title = 'Session Expired'
			// Clear persisted session on expiry
			localStorage.removeItem(SESSION_STORAGE_KEY)
			updateSession(defaultSession)
			trigger('sessionExpired')
		}

		// NOTE: Uncomment below to display the session timeout for testing purposes...
		document.title = 'Session ' + sessionTimeoutDisplay.minutes + ':' + sessionTimeoutDisplay.seconds
	}

	const resetSessionTimer = (loginSession) =>
	{
		clearInterval(sessionTimer)

		sessionTimeoutSeconds = sessionDurationInSeconds(loginSession.user.userSession.sessionExpiry)
		secondsBeforePrompt = Math.abs(sessionTimeoutSeconds * 0.75)

		sessionTimer = setInterval(countdownSession,1000)
	}

	const setSession = (session) =>
	{
		setLoginSession(previousLoginSession =>
		{
			let currentLoginSession = { ...previousLoginSession }
			currentLoginSession = session

			clearInterval(sessionTimer)
			if (currentLoginSession.isAuthenticated)
			{
				document.title = 'Garage Sale'
				try
				{
					localStorage.setItem('userName', currentLoginSession.user.userName)
				} catch {}

				// Persist session to localStorage
				persistSession(currentLoginSession)
				resetSessionTimer(currentLoginSession)
			}
			else
			{
				// Clear persisted session on logout
				persistSession(currentLoginSession)
			}

			return currentLoginSession
		})
	}

	const updateSession = (session) =>
	{
		setLoginSession(session)

		if (!session.isAuthenticated || session.user.isLockedOut)
		{
			clearInterval(sessionTimer)
			// Clear persisted session
			persistSession(session)
		}
		else
		{
			// Update persisted session with new expiry
			persistSession(session)
			resetSessionTimer(session)
		}
	}

	const resetSession = (session) =>
	{
		clearInterval(sessionTimer)
		setLoginSession(session)
		// Clear persisted session
		localStorage.removeItem(SESSION_STORAGE_KEY)
	}

	return <Provider value={{
		loginSession,
		setSession,
		updateSession,
		resetSession,
		defaultSession,
		showAdminTestingControls,
		setShowAdminTestingControls,
	}}>
		{ props.children }
	</Provider>
}

export { SessionProvider, SessionContext }
